import { useCallback, useEffect, useMemo, useState } from "react";
import { ActiveFarmContext } from "./context";
import { useApiContext } from "../ApiContext";
import { useUserContext } from "../UserContext";
import scopedStorage from "@/utils/scopedStorage";

function parseStoredValue(value) {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && parsed.id != null) {
      return String(parsed.id);
    }
    if (parsed != null && typeof parsed !== "object") {
      return String(parsed);
    }
  } catch {
    return String(value);
  }

  return null;
}

function parseStoredSelection(value) {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      farmId:
        parsed.farmId != null
          ? String(parsed.farmId)
          : parsed.farm?.id != null
            ? String(parsed.farm.id)
            : null,
      fieldId:
        parsed.fieldId != null
          ? String(parsed.fieldId)
          : parsed.field?.id != null
            ? String(parsed.field.id)
            : null,
    };
  } catch {
    return null;
  }
}

export const ActiveFarmProvider = ({ children }) => {
  const { userSub } = useUserContext();
  const { api } = useApiContext();
  const [farms, setFarms] = useState([]);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [farmsError, setFarmsError] = useState(null);
  const [selection, setSelection] = useState({ farmId: null, fieldId: null });
  const [hasHydratedSelection, setHasHydratedSelection] = useState(false);
  const [hasLoadedFarms, setHasLoadedFarms] = useState(false);

  const refreshFarms = useCallback(async () => {
    if (!api || !userSub) {
      setFarms([]);
      setFarmsError(null);
      setHasLoadedFarms(false);
      return;
    }

    try {
      setFarmsLoading(true);
      setFarmsError(null);
      const nextFarms = await api.getFarms();
      setFarms(nextFarms);
    } catch (err) {
      console.error("Failed to load farms:", err);
      setFarms([]);
      setFarmsError(err.message || "Failed to load farms");
    } finally {
      setFarmsLoading(false);
      setHasLoadedFarms(true);
    }
  }, [api, userSub]);

  useEffect(() => {
    if (!userSub) {
      setFarms([]);
      setFarmsError(null);
      setSelection({ farmId: null, fieldId: null });
      setHasHydratedSelection(true);
      return;
    }

    const storedSelection = parseStoredSelection(
      scopedStorage.get(userSub, "selection")
    );

    if (storedSelection) {
      setSelection(storedSelection);
      setHasHydratedSelection(true);
      return;
    }

    const farmId = parseStoredValue(scopedStorage.get(userSub, "activeFarm"));
    const fieldId = farmId
      ? parseStoredValue(
          scopedStorage.get(userSub, `lastSelectedField:${farmId}`)
        )
      : null;

    setSelection({ farmId, fieldId });
    setHasHydratedSelection(true);
  }, [userSub]);

  useEffect(() => {
    refreshFarms();

    if (!api) {
      return undefined;
    }

    const handlePostFarm = () => {
      refreshFarms();
    };

    api.addEventListener("postFarm", handlePostFarm);

    return () => {
      api.removeEventListener("postFarm", handlePostFarm);
    };
  }, [api, refreshFarms]);

  useEffect(() => {
    if (!userSub || !hasHydratedSelection) {
      return;
    }

    if (selection.farmId !== null || selection.fieldId !== null) {
      scopedStorage.set(userSub, "selection", JSON.stringify(selection));
    } else {
      scopedStorage.remove(userSub, "selection");
    }
  }, [hasHydratedSelection, selection, userSub]);

  const activeFarmId = selection.farmId;
  const lastSelectedFieldId = selection.fieldId;

  const activeFarm = useMemo(
    () =>
      farms.find((farm) => String(farm.id) === String(activeFarmId || "")) ||
      null,
    [activeFarmId, farms]
  );

  useEffect(() => {
    if (
      !hasHydratedSelection ||
      !hasLoadedFarms ||
      !activeFarmId ||
      farmsLoading ||
      farmsError
    ) {
      return;
    }

    const exists = farms.some(
      (farm) => String(farm.id) === String(activeFarmId)
    );

    if (!exists) {
      setSelection((current) => ({
        ...current,
        farmId: null,
        fieldId: null,
      }));
    }
  }, [
    activeFarmId,
    farms,
    farmsError,
    farmsLoading,
    hasHydratedSelection,
    hasLoadedFarms,
  ]);

  const setActiveFarm = useCallback((farmOrId) => {
    if (!farmOrId) {
      setSelection({ farmId: null, fieldId: null });
      return;
    }

    const nextFarmId =
      typeof farmOrId === "object" ? String(farmOrId.id) : String(farmOrId);

    setSelection((current) => ({
      farmId: nextFarmId,
      fieldId: current.farmId === nextFarmId ? current.fieldId : null,
    }));
  }, []);

  const setLastSelectedFieldId = useCallback((fieldId) => {
    setSelection((current) => ({
      ...current,
      fieldId: fieldId ? String(fieldId) : null,
    }));
  }, []);

  const value = useMemo(
    () => ({
      farms,
      farmsLoading,
      farmsError,
      refreshFarms,
      activeFarm,
      activeFarmId,
      hasLoadedFarms,
      setActiveFarm,
      lastSelectedFieldId,
      setLastSelectedFieldId,
    }),
    [
      farms,
      farmsLoading,
      farmsError,
      refreshFarms,
      activeFarm,
      activeFarmId,
      hasLoadedFarms,
      setActiveFarm,
      lastSelectedFieldId,
      setLastSelectedFieldId,
    ]
  );

  return (
    <ActiveFarmContext.Provider value={value}>
      {children}
    </ActiveFarmContext.Provider>
  );
};
